/**
 * @fileoverview Number-formatting helpers for the banking feature.
 * @description Ported from the InsightBox Banking design system's own `data/derived.ts`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

/**
 * @description Formats a value in Rs Crore.
 * @param {number} value - The amount, in Cr.
 * @param {number} [decimals] - Decimal places. Defaults to 2.
 * @returns {string} E.g. "₹128.40 Cr".
 */
export function formatCr(value: number, decimals = 2): string {
  return `₹${value.toFixed(decimals)} Cr`
}

/**
 * @description Formats a value in Rs Lacs.
 * @param {number} value - The amount, in Lacs.
 * @param {number} [decimals] - Decimal places. Defaults to 2.
 * @returns {string} E.g. "₹88.00 L".
 */
export function formatLacs(value: number, decimals = 2): string {
  return `₹${value.toFixed(decimals)} L`
}

/**
 * @description Formats a plain percentage.
 * @param {number} value - The percentage value.
 * @param {number} [decimals] - Decimal places. Defaults to 2.
 * @returns {string} E.g. "12.34%".
 */
export function formatPct(value: number, decimals = 2): string {
  return `${value.toFixed(decimals)}%`
}

/**
 * @description Formats a count with Indian-locale digit grouping.
 * @param {number} value - The count to format.
 * @returns {string} E.g. "1,23,456".
 */
export function formatNumber(value: number): string {
  return Math.round(value).toLocaleString('en-IN')
}

/**
 * @description Formats a rupee amount with the ₹ symbol and Indian-locale digit grouping.
 * @param {number} value - The amount, in rupees.
 * @returns {string} E.g. "₹1,23,456".
 */
export function formatRupee(value: number): string {
  return `₹${Math.round(value).toLocaleString('en-IN')}`
}

/**
 * @description Formats a signed percentage-point delta.
 * @param {number} value - The delta, in percentage points.
 * @param {number} [decimals] - Decimal places. Defaults to 2.
 * @returns {string} E.g. "+1.20 pp" or "-0.50 pp".
 */
export function formatPP(value: number, decimals = 2): string {
  const sign = value >= 0 ? '+' : ''
  return `${sign}${value.toFixed(decimals)} pp`
}

/**
 * @description Formats a signed percentage.
 * @param {number} value - The percentage value.
 * @param {number} [decimals] - Decimal places. Defaults to 2.
 * @returns {string} E.g. "+12.34%" or "-5.00%".
 */
export function formatSignedPct(value: number, decimals = 2): string {
  const sign = value >= 0 ? '+' : ''
  return `${sign}${value.toFixed(decimals)}%`
}
